import { CacheType } from '@descomplica/caching-browser';
import {
  BaseHttpGateway,
  cache,
  CollaborationResult,
  RecognitionManager,
} from '@descomplica/microfrontend-core';
import { injectable } from 'tsyringe';

import type {
  ApplicationDto,
  ClassifierDto,
  IPegasusGateway,
  LiveStreamingDto,
  QuizDto,
  StudyPlanDto,
  UserDto,
} from '@/application/interfaces/pegasus.gateway';

import envs from '../config/envs';

@injectable()
export class PegasusGateway extends BaseHttpGateway implements IPegasusGateway {
  constructor(private readonly recognitionManager: RecognitionManager) {
    super();
  }

  async getIdentifiedUser(): Promise<CollaborationResult<UserDto>> {
    const { data } = await this.axios.get(`/identified-user`);
    return data;
  }

  async getApplication(): Promise<CollaborationResult<ApplicationDto>> {
    const url = `/application`;
    const { data } = await this.axios.get(url);
    return data;
  }

  @cache(`getLibraryByGuid`, {
    ttl: 60000,
  })
  async getLibraryByClassifierGuid(
    classifierGuid: string,
  ): Promise<CollaborationResult<ClassifierDto[]>> {
    const { data } = await this.axios.get(`/library/${classifierGuid}`);
    return data;
  }

  @cache(`getHighestClassificationLevels`, {
    ttl: 60000,
  })
  async getHighestClassificationLevels(): Promise<CollaborationResult<ClassifierDto[]>> {
    const { data } = await this.axios.get(`/highest-level-classifiers`);
    return data;
  }

  @cache(`getStudyPlan`, {
    ttl: 60000,
  })
  async getStudyPlan(guid: string): Promise<CollaborationResult<StudyPlanDto>> {
    const { data } = await this.axios.get(`/study-plan/${guid}`);
    return data;
  }

  @cache(`getLiveStreamingByGuid`, {
    ttl: 60000,
  })
  async getLiveStreamingByGuid(guid: string): Promise<CollaborationResult<LiveStreamingDto>> {
    const { data } = await this.axios.get(`/library/streaming/live/${guid}`);
    return data;
  }

  @cache(`getExercisesByClassifier`, {
    ttl: 60000,
  })
  async getExercisesByClassifier(guid: string): Promise<CollaborationResult<QuizDto[]>> {
    const { data } = await this.axios.get(`/exercises/classifier/${guid}`);
    return data;
  }

  protected getEndpoint(): string {
    return `${envs.pegasusUrl}/application-management`;
  }

  protected getCacheType(): CacheType {
    return CacheType.LOCALSTORAGE;
  }

  protected getCacheNamespace(): string {
    return 'descomplica-io-frontend';
  }

  protected getBearerToken(): string {
    return this.recognitionManager.getAccessToken();
  }

  protected getHeaders(): object {
    const headers = {};
    headers['Cache-Control'] = 'no-cache';
    headers['Pragma'] = 'no-cache';
    headers['Expires'] = '0';
    return headers;
  }
}
